import { html, nothing } from "lit";

import { customElement, state } from "lit/decorators.js";

import { DataQueryController } from "ui-common/data-query-controller";
import { SignalElement, inject } from "ui-common/di";
import type { IAuthState } from "ui-common/services/auth/types";
import "ui-components/button/wow-button";
import "ui-components/group-button/group-button";
import "ui-components/post-card/post-card";
import { featureFlagsQueryOptions } from "../../queries/feature-flags";

import { styles } from "./posts-page.css";

import "../../components/create-post-modal/create-post-modal";
import "ui-components/feedback/create-feedback-modal";
import "../../components/page-content/page-content";
import "../../components/posts-mobile-header/posts-mobile-header";
import "../../components/worlds-layout/worlds-layout";
import { globalStyles } from "../../components/worlds-layout/worlds-layout-global.css";
import { postsQueryOptions } from "../../queries/posts";
import { navigateTo } from "../../router";
import { t } from "../../translations/i18n";

type PostFilter = "all" | "live" | "draft" | "planned";

@customElement("posts-page")
export class PostsPage extends SignalElement {
  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  static override styles = [styles, globalStyles];

  @state()
  private selectedFilter: PostFilter = "live";

  @state()
  private showCreatePostModal = false;

  @state()
  private showEditPostModal = false;

  @state()
  private showFeedbackModal = false;

  @state()
  private editingPostId?: string;

  private postsQuery = new DataQueryController(this, () => postsQueryOptions);

  private featureFlagsQuery = new DataQueryController(this, () => featureFlagsQueryOptions);

  private handleFilterChange(filter: PostFilter) {
    this.selectedFilter = filter;
  }

  private handleOpenCreateModal = () => {
    this.showCreatePostModal = true;
  };

  private handleCloseCreateModal = () => {
    this.showCreatePostModal = false;
  };

  private handlePreviewPublish = () => {
    this.showCreatePostModal = false;
  };

  private handlePostCreateSuccess = () => {
    if (this.postsQuery.result.data?.posts?.length === 1) {
      this.showFeedbackModal = true;
    }
  };

  private handlePostClick = (event: CustomEvent) => {
    const { postId } = event.detail;
    this.editingPostId = postId;
    this.showEditPostModal = true;
  };

  private handleCloseEditModal = () => {
    this.showEditPostModal = false;
    this.editingPostId = undefined;
  };

  private handleOpenWorlds = () => {
    const handle = this.authState.currentUser.get()?.handle;

    if (handle) window.open(`/${handle}/my-world`, "_self", "noopener,noreferrer");
  };

  private renderMainContent() {
    const { data: postsData, isPending, error } = this.postsQuery.result;

    if (isPending) {
      return html`
        <div class="main-content">
          ${this.renderToggleButtons()}
          <div class="posts-grid">
            <skeleton-rect class="skeleton-rectangle" width="150px" height="270px"></skeleton-rect>
            <skeleton-rect class="skeleton-rectangle" width="150px" height="270px"></skeleton-rect>
            <skeleton-rect class="skeleton-rectangle" width="150px" height="270px"></skeleton-rect>
            <skeleton-rect class="skeleton-rectangle" width="150px" height="270px"></skeleton-rect>
            <skeleton-rect class="skeleton-rectangle" width="150px" height="270px"></skeleton-rect>
            <skeleton-rect class="skeleton-rectangle" width="150px" height="270px"></skeleton-rect>
          </div>
        </div>
      `;
    }

    if (error) {
      return html`
        <div class="main-content">
          ${this.renderToggleButtons()}
          <div class="error">Error loading posts: ${error.message}</div>
        </div>
      `;
    }

    if (!postsData?.posts?.length) {
      return html` <div class="main-content">${this.renderToggleButtons()} ${this.renderEmptyState()}</div> `;
    }

    // Filter posts based on selected filter
    const filteredPosts =
      this.selectedFilter === "live"
        ? postsData.posts
        : postsData.posts.filter((post) => post.status === this.selectedFilter);

    if (!filteredPosts.length) {
      return html`
        <div class="main-content">
          ${this.renderToggleButtons()}
          <div class="no-posts">No ${this.selectedFilter} posts found.</div>
        </div>
      `;
    }

    const features = this.featureFlagsQuery.result?.data?.features ?? [];
    const hasPremiumFeatures = features.includes("subscription") || features.includes("one-time-purchase");

    return html`
      <div class="main-content">
        ${this.renderToggleButtons()}
        <div class="posts-grid" @post-click=${this.handlePostClick}>
          ${filteredPosts.map(
            (post) => html`
              <post-card
                .id=${post.id}
                .teaser=${post.teaser || ""}
                .mediaUrl=${post.mediaUrl || ""}
                .status=${post.status}
                .contentTier=${hasPremiumFeatures ? post.contentTier : "free"}
              ></post-card>
            `,
          )}
        </div>
      </div>
    `;
  }

  private renderEmptyState() {
    return html`
      <div class="empty-state">
        <h2 class="empty-state-title">${t("posts.emptyStateTitle")}</h2>
        <p class="empty-state-subtitle">${t("posts.emptyStateSubtitle")}</p>
        <wow-button variant="primary" size="medium" @click=${this.handleOpenCreateModal}>
          ${t("posts.addPosts")}
        </wow-button>
      </div>
    `;
  }

  private renderToggleButtons() {
    const { data: postsData } = this.postsQuery.result;
    const counts = postsData?.counts || { live: 0, draft: 0, planned: 0, free: 0, premium: 0 };

    return html`
      <div class="filter-buttons">
        <wow-button
          variant=${this.selectedFilter === "live" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("live")}
        >
          Live (${counts.live})
        </wow-button>
        <wow-button
          variant=${this.selectedFilter === "draft" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("draft")}
        >
          Draft (${counts.draft})
        </wow-button>
        <wow-button
          variant=${this.selectedFilter === "planned" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("planned")}
        >
          Planned (${counts.planned})
        </wow-button>
      </div>
    `;
  }

  protected override render() {
    return html`
      <page-content>
        <!-- Navigation buttons slot -->
        <group-button slot="title">
          <wow-button variant="subtle" size="small" @click=${() => navigateTo("worlds")}>
            ${t("posts.worldsTab")}
          </wow-button>
          <wow-button variant="primary" size="small">${t("posts.postsTab")}</wow-button>
        </group-button>

        <!-- Action buttons slot -->
        <div slot="actions" class="action-buttons">
          <view-primary-buttons
            @primary-click=${this.handleOpenCreateModal}
            @view-click=${this.handleOpenWorlds}
          ></view-primary-buttons>
        </div>

        <!-- Worlds Layout Component -->
        <worlds-layout>
          <!-- Main content slot -->
          <div slot="main-content">${this.renderMainContent()}</div>

          <!-- Secondary content slot -->
          <div slot="secondary-content">
            <h3 class="secondary-title">${t("posts.createEngageTitle")}</h3>
            <p class="secondary-description">${t("posts.createEngageDescription")}</p>
          </div>
        </worlds-layout>

        ${this.showCreatePostModal
          ? html`
              <create-post-modal
                @modal-close=${this.handleCloseCreateModal}
                @preview-publish=${this.handlePreviewPublish}
                @publish-success=${this.handlePostCreateSuccess}
              ></create-post-modal>
            `
          : nothing}
        ${this.showEditPostModal && this.editingPostId
          ? html`
              <create-post-modal
                .postId=${this.editingPostId}
                @modal-close=${this.handleCloseEditModal}
              ></create-post-modal>
            `
          : nothing}
        ${this.showFeedbackModal
          ? html`<create-feedback-modal
              headerText=${t("feedback.headerText")}
              sentimentMessage=${t("feedback.sentimentMessage", { context: "post" })}
              sentimentPrompt=${t("feedback.sentimentPrompt")}
              labelPrompt=${t("feedback.labelPrompt")}
              labelPlaceholder=${t("feedback.labelPlaceholder")}
              labelError=${t("feedback.labelError")}
              successMessage=${t("feedback.successMessage")}
              footerButtonText=${t("feedback.footerButtonText")}
              footerButtonLoadingText=${t("feedback.footerButtonLoadingText")}
              @close=${() => (this.showFeedbackModal = false)}
            ></create-feedback-modal>`
          : nothing}
      </page-content>
    `;
  }
}
